import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { companyAPI } from '../utils/api';
import { Phone, Mail, MapPin, Facebook, Linkedin, Twitter, Instagram, MessageCircle } from 'lucide-react';

const MarketingFooter = () => {
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await companyAPI.getInfo();
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  return (
    <>
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img src={companyInfo?.logo_url || '/ecoway-logo.jpeg'} alt="Ecoway Cabs Logo" className="h-16 w-16 object-contain bg-white rounded-lg p-1" />
              <div>
                <h3 className="text-2xl font-bold">{companyInfo?.company_name || 'Ecoway Cabs'}</h3>
                <p className="text-gray-400 text-sm">Since {companyInfo?.established_year || 2012}</p>
              </div>
            </div>
            <p className="text-gray-300 mb-4">{companyInfo?.tagline}</p>
            <p className="text-sm text-gray-400">Providing cost-effective, comfortable, and elegant transportation services across India.</p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Services</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/quote" className="text-gray-300 hover:text-white transition-colors">Get Quote</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Phone className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                <a href={`tel:${companyInfo?.contact_phone}`} className="text-gray-300 hover:text-white transition-colors">
                  {companyInfo?.contact_phone || '+91 7874506050'}
                </a>
              </li>
              <li className="flex items-start">
                <Mail className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                <a href={`mailto:${companyInfo?.contact_email}`} className="text-gray-300 hover:text-white transition-colors break-all">
                  {companyInfo?.contact_email || 'support@ecowaycabs.com'}
                </a>
              </li>
              <li className="flex items-start">
                <MapPin className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">{companyInfo?.address || 'Gujarat, India'}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} {companyInfo?.company_name || 'Ecoway Cabs'}. All rights reserved.
            </div>
            <div className="flex space-x-4">
              {companyInfo?.social_links?.facebook && (
                <a href={companyInfo.social_links.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors" data-testid="footer-facebook-link">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {companyInfo?.social_links?.linkedin && (
                <a href={companyInfo.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors" data-testid="footer-linkedin-link">
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {companyInfo?.social_links?.twitter && (
                <a href={companyInfo.social_links.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors" data-testid="footer-twitter-link">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {companyInfo?.social_links?.instagram && (
                <a href={companyInfo.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-400 transition-colors" data-testid="footer-instagram-link">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {companyInfo?.social_links?.whatsapp && (
                <a href={companyInfo.social_links.whatsapp} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-400 transition-colors" data-testid="footer-whatsapp-link">
                  <MessageCircle className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>

    {companyInfo?.social_links?.whatsapp && (
      <a
        href={companyInfo.social_links.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        data-testid="floating-whatsapp-button"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] text-white shadow-lg shadow-black/25 hover:bg-[#1ebe5b] hover:scale-105 transition-transform duration-200"
      >
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-60 animate-ping" />
        <MessageCircle className="h-7 w-7 relative" />
      </a>
    )}
    </>
  );
};

export default MarketingFooter;